export const AUDIO_GROUPS = ['ui', 'sfx', 'ambience', 'music', 'voice'] as const;

export type AudioGroup = (typeof AUDIO_GROUPS)[number];
export type AudioLane = 'left' | 'center' | 'right';
export type AudioContextStatus = AudioContextState | 'unavailable' | 'interrupted';
export type AudioGroupVolumes = Readonly<Record<AudioGroup, number>>;

export interface AudioSystemState {
  readonly supported: boolean;
  readonly unlocked: boolean;
  readonly disposed: boolean;
  readonly muted: boolean;
  readonly paused: boolean;
  readonly pageHidden: boolean;
  readonly ambienceEnabled: boolean;
  readonly ambiencePlaying: boolean;
  readonly contextState: AudioContextStatus;
  readonly masterVolume: number;
  readonly groupVolumes: AudioGroupVolumes;
  readonly activeVoices: number;
  readonly maxVoices: number;
}

export interface AudioSystemOptions {
  readonly masterVolume?: number;
  readonly groupVolumes?: Partial<Record<AudioGroup, number>>;
  readonly muted?: boolean;
  readonly ambienceEnabled?: boolean;
  /** Maximum concurrent one-shot voices. Defaults to 24. */
  readonly maxVoices?: number;
  /** Installs pointer/key gesture unlock listeners. Defaults to true. */
  readonly autoUnlock?: boolean;
  readonly onStateChange?: AudioSystemStateListener;
  readonly onError?: (error: unknown) => void;
}

export type AudioSystemStateListener = (state: AudioSystemState) => void;
export type AudioSystemUnsubscribe = () => void;

interface ActiveVoice {
  readonly id: number;
  readonly priority: number;
  readonly startedAt: number;
  readonly input: GainNode;
  readonly panner: StereoPannerNode;
  readonly sources: Set<AudioScheduledSourceNode>;
}

interface AmbienceHandle {
  readonly mix: GainNode;
  readonly sources: readonly AudioScheduledSourceNode[];
  readonly nodes: readonly AudioNode[];
}

interface ToneOptions {
  readonly delay?: number;
  readonly duration: number;
  readonly frequency: number;
  readonly endFrequency?: number;
  readonly gain: number;
  readonly attack?: number;
  readonly type?: OscillatorType;
  readonly detune?: number;
}

interface NoiseOptions {
  readonly delay?: number;
  readonly duration: number;
  readonly gain: number;
  readonly attack?: number;
  readonly kind?: 'white' | 'brown';
  readonly filterType?: BiquadFilterType;
  readonly frequency?: number;
  readonly q?: number;
  readonly playbackRate?: number;
}

const DEFAULT_GROUP_VOLUMES: AudioGroupVolumes = Object.freeze({
  ui: 0.72,
  sfx: 0.78,
  ambience: 0.32,
  music: 0.65,
  voice: 0.86,
});

const SILENCE = 0.0001;

/**
 * Procedural Web Audio feedback for the coin-pusher.
 *
 * Context creation is deferred until a user gesture. All high-frequency
 * physical feedback is cooldown- and polyphony-limited; no update-loop calls
 * are needed for ambience or cleanup.
 */
export class AudioSystem {
  private readonly onError: AudioSystemOptions['onError'];
  private readonly maxVoicesValue: number;
  private readonly listeners = new Set<AudioSystemStateListener>();
  private readonly activeVoices = new Map<number, ActiveVoice>();
  private readonly cooldowns = new Map<string, number>();
  private readonly variants = new Map<string, number>();
  private readonly groupVolumesValue: Record<AudioGroup, number>;
  private readonly groupGains = new Map<AudioGroup, GainNode>();

  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private whiteNoise: AudioBuffer | null = null;
  private brownNoise: AudioBuffer | null = null;
  private ambience: AmbienceHandle | null = null;
  private unlockPromise: Promise<void> | null = null;
  private nextVoiceId = 1;
  private supportedValue: boolean;
  private unlockedValue = false;
  private disposedValue = false;
  private mutedValue: boolean;
  private pausedValue = false;
  private pageHiddenValue: boolean;
  private ambienceEnabledValue: boolean;
  private masterVolumeValue: number;
  private unlockListenersInstalled = false;
  private visibilityListenerInstalled = false;

  private readonly handleUnlockGesture = (): void => {
    void this.unlock();
  };

  private readonly handleVisibilityChange = (): void => {
    const documentTarget = getDocument();
    this.pageHiddenValue = documentTarget?.hidden ?? false;
    if (this.pageHiddenValue) {
      this.stopAmbience(0);
      if (this.context?.state === 'running') {
        void this.context.suspend().catch((error: unknown) => this.reportError(error));
      }
    } else if (this.unlockedValue && !this.pausedValue) {
      void this.resumeContextAndAmbience();
    }
    this.notifyState();
  };

  constructor(options: AudioSystemOptions = {}) {
    this.onError = options.onError;
    this.masterVolumeValue = readVolume(options.masterVolume ?? 0.82, 'masterVolume');
    this.mutedValue = options.muted ?? false;
    this.ambienceEnabledValue = options.ambienceEnabled ?? true;
    this.maxVoicesValue = readIntegerInRange(options.maxVoices ?? 24, 4, 64, 'maxVoices');
    this.groupVolumesValue = { ...DEFAULT_GROUP_VOLUMES };
    for (const group of AUDIO_GROUPS) {
      const override = options.groupVolumes?.[group];
      if (override !== undefined) {
        this.groupVolumesValue[group] = readVolume(override, `groupVolumes.${group}`);
      }
    }

    this.supportedValue = getAudioContextConstructor() !== null;
    this.pageHiddenValue = getDocument()?.hidden ?? false;
    if (options.onStateChange) this.listeners.add(options.onStateChange);
    this.installVisibilityListener();
    if (options.autoUnlock ?? true) this.installUnlockListeners();
  }

  get state(): AudioSystemState {
    return this.getState();
  }

  get muted(): boolean {
    return this.mutedValue;
  }

  get paused(): boolean {
    return this.pausedValue;
  }

  get unlocked(): boolean {
    return this.unlockedValue;
  }

  getState(): AudioSystemState {
    return Object.freeze({
      supported: this.supportedValue,
      unlocked: this.unlockedValue,
      disposed: this.disposedValue,
      muted: this.mutedValue,
      paused: this.pausedValue,
      pageHidden: this.pageHiddenValue,
      ambienceEnabled: this.ambienceEnabledValue,
      ambiencePlaying: this.ambience !== null,
      contextState: this.readContextState(),
      masterVolume: this.masterVolumeValue,
      groupVolumes: Object.freeze({ ...this.groupVolumesValue }),
      activeVoices: this.activeVoices.size,
      maxVoices: this.maxVoicesValue,
    });
  }

  subscribeState(
    listener: AudioSystemStateListener,
    emitImmediately = true,
  ): AudioSystemUnsubscribe {
    this.assertActive();
    this.listeners.add(listener);
    if (emitImmediately) this.invokeSafely(() => listener(this.getState()));
    return () => this.listeners.delete(listener);
  }

  async unlock(): Promise<void> {
    this.assertActive();
    if (!this.supportedValue) return;
    if (this.context?.state === 'running') {
      this.unlockedValue = true;
      this.removeUnlockListeners();
      this.startAmbience();
      this.notifyState();
      return;
    }
    if (this.unlockPromise) return this.unlockPromise;

    this.unlockPromise = this.performUnlock().finally(() => {
      this.unlockPromise = null;
    });
    return this.unlockPromise;
  }

  setMuted(muted: boolean): void {
    this.assertActive();
    if (muted === this.mutedValue) return;
    this.mutedValue = muted;
    this.applyMasterGain(0.035);
    if (muted) this.stopAmbience(0.06);
    else this.startAmbience();
    this.notifyState();
  }

  toggleMute(): boolean {
    this.setMuted(!this.mutedValue);
    return this.mutedValue;
  }

  setMasterVolume(volume: number): void {
    this.assertActive();
    this.masterVolumeValue = readVolume(volume, 'masterVolume');
    this.applyMasterGain(0.035);
    this.notifyState();
  }

  setGroupVolume(group: AudioGroup, volume: number): void {
    this.assertActive();
    assertAudioGroup(group);
    this.groupVolumesValue[group] = readVolume(volume, `groupVolumes.${group}`);
    const node = this.groupGains.get(group);
    if (node && this.context) rampGain(node.gain, this.context.currentTime, volume, 0.035);
    this.notifyState();
  }

  setAmbienceEnabled(enabled: boolean): void {
    this.assertActive();
    if (enabled === this.ambienceEnabledValue) return;
    this.ambienceEnabledValue = enabled;
    if (enabled) this.startAmbience();
    else this.stopAmbience(0.12);
    this.notifyState();
  }

  /** Soft premium mechanical tick for general buttons. */
  buttonClick(): void {
    const context = this.prepareSound('button', 0.035, true);
    if (!context) return;
    const voice = this.beginVoice(context, 'ui', 3, 0);
    if (!voice) return;
    const variant = this.nextVariant('button', 3);
    this.addTone(voice, {
      duration: 0.07,
      frequency: 430 + variant * 24,
      endFrequency: 310 + variant * 18,
      gain: 0.075,
      attack: 0.004,
      type: 'triangle',
    });
    this.addNoise(voice, {
      duration: 0.035,
      gain: 0.018,
      filterType: 'highpass',
      frequency: 3_100,
      q: 0.7,
    });
  }

  /** Warm two-note confirmation. */
  confirm(): void {
    const context = this.prepareSound('confirm', 0.08, true);
    if (!context) return;
    const voice = this.beginVoice(context, 'ui', 4, 0);
    if (!voice) return;
    this.addTone(voice, {
      duration: 0.12,
      frequency: 520,
      endFrequency: 590,
      gain: 0.07,
      type: 'sine',
    });
    this.addTone(voice, {
      delay: 0.075,
      duration: 0.16,
      frequency: 780,
      endFrequency: 910,
      gain: 0.06,
      type: 'triangle',
    });
  }

  /** Pitch and stereo position identify the selected insertion lane. */
  laneSelect(lane: AudioLane): void {
    assertLane(lane);
    const context = this.prepareSound(`lane-${lane}`, 0.045, true);
    if (!context) return;
    const laneIndex = lane === 'left' ? 0 : lane === 'center' ? 1 : 2;
    const voice = this.beginVoice(context, 'ui', 3, (laneIndex - 1) * 0.56);
    if (!voice) return;
    this.addTone(voice, {
      duration: 0.105,
      frequency: 420 + laneIndex * 75,
      endFrequency: 510 + laneIndex * 86,
      gain: 0.065,
      type: 'triangle',
    });
    this.addTone(voice, {
      duration: 0.065,
      frequency: 1_260 + laneIndex * 120,
      gain: 0.02,
      type: 'sine',
    });
  }

  /** Slot clack followed by a small metallic coin ring. */
  coinInsert(lane: AudioLane = 'center'): void {
    assertLane(lane);
    const context = this.prepareSound('coin-insert', 0.07);
    if (!context) return;
    const pan = lane === 'left' ? -0.5 : lane === 'right' ? 0.5 : 0;
    const voice = this.beginVoice(context, 'sfx', 4, pan);
    if (!voice) return;
    const variant = this.nextVariant('coin-insert', 4);
    this.addNoise(voice, {
      duration: 0.045,
      gain: 0.05,
      filterType: 'bandpass',
      frequency: 1_300 + variant * 130,
      q: 1.1,
    });
    this.addTone(voice, {
      delay: 0.018,
      duration: 0.22,
      frequency: 1_580 + variant * 95,
      endFrequency: 1_210 + variant * 70,
      gain: 0.052,
      attack: 0.002,
      type: 'sine',
    });
    this.addTone(voice, {
      delay: 0.022,
      duration: 0.11,
      frequency: 2_740 + variant * 135,
      gain: 0.025,
      attack: 0.002,
      type: 'triangle',
    });
  }

  /** Rate-limited coin-on-metal collision; intensity and pan are normalized. */
  metalImpact(intensity = 0.5, pan = 0): void {
    const amount = clamp(intensity, 0, 1);
    if (amount < 0.04) return;
    const context = this.prepareSound('metal-impact', 0.038);
    if (!context) return;
    const voice = this.beginVoice(context, 'sfx', 1, clamp(pan, -1, 1));
    if (!voice) return;
    const variant = this.nextVariant('metal-impact', 5);
    this.addNoise(voice, {
      duration: 0.055 + amount * 0.045,
      gain: 0.018 + amount * 0.055,
      filterType: 'bandpass',
      frequency: 1_050 + variant * 180,
      q: 1.8,
      playbackRate: 0.92 + variant * 0.035,
    });
    this.addTone(voice, {
      duration: 0.11 + amount * 0.12,
      frequency: 680 + variant * 87,
      endFrequency: 520 + variant * 64,
      gain: 0.016 + amount * 0.043,
      attack: 0.002,
      type: 'sine',
    });
  }

  /** Low-priority filtered movement bed for several coins settling together. */
  stackRustle(intensity = 0.5, pan = 0): void {
    const amount = clamp(intensity, 0, 1);
    if (amount < 0.08) return;
    const context = this.prepareSound('stack-rustle', 0.095);
    if (!context) return;
    const voice = this.beginVoice(context, 'sfx', 0, clamp(pan, -1, 1));
    if (!voice) return;
    const variant = this.nextVariant('stack-rustle', 4);
    this.addNoise(voice, {
      duration: 0.16 + amount * 0.16,
      gain: 0.012 + amount * 0.038,
      attack: 0.015,
      kind: 'brown',
      filterType: 'bandpass',
      frequency: 720 + variant * 125,
      q: 0.65,
      playbackRate: 0.84 + variant * 0.06,
    });
    this.addNoise(voice, {
      delay: 0.045,
      duration: 0.085,
      gain: 0.008 + amount * 0.018,
      filterType: 'highpass',
      frequency: 2_600,
      q: 0.5,
    });
  }

  coinWin(value = 1, pan = 0): void {
    const context = this.prepareSound('coin-win', 0.055);
    if (!context) return;
    const voice = this.beginVoice(context, 'sfx', 5, clamp(pan, -1, 1));
    if (!voice) return;
    const pitchLift = Math.min(180, Math.log2(Math.max(1, value)) * 28);
    const variant = this.nextVariant('coin-win', 3);
    const root = 690 + pitchLift + variant * 16;
    [1, 1.25, 1.5].forEach((ratio, index) => {
      this.addTone(voice, {
        delay: index * 0.055,
        duration: 0.21,
        frequency: root * ratio,
        endFrequency: root * ratio * 1.06,
        gain: 0.052 - index * 0.007,
        attack: 0.004,
        type: index === 0 ? 'triangle' : 'sine',
      });
    });
    this.addNoise(voice, {
      duration: 0.08,
      gain: 0.018,
      filterType: 'highpass',
      frequency: 4_200,
      q: 0.8,
    });
  }

  cardCollect(denomination = 50): void {
    const context = this.prepareSound('card-collect', 0.14);
    if (!context) return;
    const voice = this.beginVoice(context, 'sfx', 7, 0);
    if (!voice) return;
    const tier = Math.min(4, Math.max(0, Math.round(Math.log10(Math.max(50, denomination) / 50) * 2)));
    const root = 430 + tier * 38;
    [1, 1.5, 2, 2.5].forEach((ratio, index) => {
      this.addTone(voice, {
        delay: index * 0.045,
        duration: 0.42 - index * 0.035,
        frequency: root * ratio,
        endFrequency: root * ratio * 1.12,
        gain: 0.042 - index * 0.004,
        attack: 0.012,
        type: index % 2 === 0 ? 'sine' : 'triangle',
        detune: index % 2 === 0 ? -4 : 4,
      });
    });
    this.addNoise(voice, {
      delay: 0.06,
      duration: 0.34,
      gain: 0.016,
      attack: 0.04,
      filterType: 'highpass',
      frequency: 5_300,
      q: 0.9,
      playbackRate: 1.28,
    });
  }

  setBonus(completedSets = 1): void {
    const context = this.prepareSound('set-bonus', 0.45);
    if (!context) return;
    const voice = this.beginVoice(context, 'sfx', 9, 0);
    if (!voice) return;
    const lift = Math.min(120, Math.max(0, completedSets - 1) * 8);
    const notes = [523, 659, 784, 1_047, 1_318];
    notes.forEach((frequency, index) => {
      this.addTone(voice, {
        delay: index * 0.09,
        duration: 0.34,
        frequency: frequency + lift,
        endFrequency: frequency + lift + 18,
        gain: 0.058 - index * 0.004,
        attack: 0.008,
        type: index < 3 ? 'triangle' : 'sine',
      });
    });
    this.addNoise(voice, {
      delay: 0.28,
      duration: 0.42,
      gain: 0.019,
      attack: 0.04,
      filterType: 'highpass',
      frequency: 5_600,
      q: 0.65,
    });
  }

  jackpotFanfare(): void {
    const context = this.prepareSound('jackpot', 2.4);
    if (!context) return;
    const voice = this.beginVoice(context, 'sfx', 12, 0);
    if (!voice) return;
    const melody = [392, 523, 659, 784, 1_047, 1_318, 1_568];
    melody.forEach((frequency, index) => {
      this.addTone(voice, {
        delay: index * 0.105,
        duration: index < 4 ? 0.34 : 0.62,
        frequency,
        endFrequency: frequency * 1.025,
        gain: 0.064 - Math.min(index, 4) * 0.004,
        attack: 0.009,
        type: index % 3 === 0 ? 'triangle' : 'sine',
        detune: index % 2 === 0 ? -3 : 3,
      });
    });
    [523, 659, 784].forEach((frequency) => {
      this.addTone(voice, {
        delay: 0.7,
        duration: 0.92,
        frequency,
        endFrequency: frequency * 1.01,
        gain: 0.034,
        attack: 0.035,
        type: 'sine',
      });
    });
    this.addNoise(voice, {
      delay: 0.42,
      duration: 1.08,
      gain: 0.022,
      attack: 0.08,
      filterType: 'highpass',
      frequency: 6_200,
      q: 0.55,
      playbackRate: 1.35,
    });
  }

  noCoins(): void {
    this.playErrorCue('no-coins', 210, 132);
  }

  error(): void {
    this.playErrorCue('error', 175, 105);
  }

  /** Plays a short pause cue, suppresses gameplay SFX, and fades ambience. */
  pause(): void {
    this.assertActive();
    if (this.pausedValue) return;
    this.playPauseCue(false);
    this.pausedValue = true;
    this.stopAmbience(0.1);
    this.notifyState();
  }

  /** Resumes gameplay SFX/ambience and plays an ascending cue. */
  resume(): void {
    this.assertActive();
    if (!this.pausedValue) return;
    this.pausedValue = false;
    void this.resumeContextAndAmbience();
    this.playPauseCue(true);
    this.notifyState();
  }

  /** Backward-compatible pickup hook used by the current prototype. */
  pickup(index: number): void {
    this.coinWin(Math.max(1, index + 1), clamp((index % 5 - 2) * 0.18, -0.6, 0.6));
  }

  startAmbience(): boolean {
    if (
      this.ambience ||
      !this.ambienceEnabledValue ||
      this.mutedValue ||
      this.pausedValue ||
      this.pageHiddenValue
    ) {
      return false;
    }
    const context = this.context;
    const group = this.groupGains.get('ambience');
    if (!context || !group || context.state === 'closed' || !this.brownNoise) return false;

    const now = context.currentTime;
    const mix = context.createGain();
    mix.gain.setValueAtTime(SILENCE, now);
    mix.gain.exponentialRampToValueAtTime(0.82, now + 0.7);
    mix.connect(group);

    const humFilter = context.createBiquadFilter();
    humFilter.type = 'lowpass';
    humFilter.frequency.value = 280;
    humFilter.Q.value = 0.55;
    humFilter.connect(mix);

    const humGain = context.createGain();
    humGain.gain.value = 0.075;
    humGain.connect(humFilter);
    const hum = context.createOscillator();
    hum.type = 'sine';
    hum.frequency.value = 52;
    hum.connect(humGain);

    const harmonicGain = context.createGain();
    harmonicGain.gain.value = 0.021;
    harmonicGain.connect(humFilter);
    const harmonic = context.createOscillator();
    harmonic.type = 'triangle';
    harmonic.frequency.value = 104;
    harmonic.detune.value = -6;
    harmonic.connect(harmonicGain);

    const machineGain = context.createGain();
    machineGain.gain.value = 0.012;
    machineGain.connect(humFilter);
    const machine = context.createOscillator();
    machine.type = 'sine';
    machine.frequency.value = 173;
    machine.connect(machineGain);

    const crowdSource = context.createBufferSource();
    crowdSource.buffer = this.brownNoise;
    crowdSource.loop = true;
    crowdSource.playbackRate.value = 0.78;
    const crowdFilter = context.createBiquadFilter();
    crowdFilter.type = 'bandpass';
    crowdFilter.frequency.value = 690;
    crowdFilter.Q.value = 0.48;
    const crowdGain = context.createGain();
    crowdGain.gain.value = 0.032;
    crowdSource.connect(crowdFilter).connect(crowdGain).connect(mix);

    const crowdLfo = context.createOscillator();
    crowdLfo.type = 'sine';
    crowdLfo.frequency.value = 0.115;
    const crowdLfoDepth = context.createGain();
    crowdLfoDepth.gain.value = 0.008;
    crowdLfo.connect(crowdLfoDepth).connect(crowdGain.gain);

    const sources: AudioScheduledSourceNode[] = [
      hum,
      harmonic,
      machine,
      crowdSource,
      crowdLfo,
    ];
    const nodes: AudioNode[] = [
      mix,
      humFilter,
      humGain,
      harmonicGain,
      machineGain,
      crowdFilter,
      crowdGain,
      crowdLfoDepth,
    ];
    for (const source of sources) source.start(now);
    this.ambience = Object.freeze({
      mix,
      sources: Object.freeze(sources),
      nodes: Object.freeze(nodes),
    });
    this.notifyState();
    return true;
  }

  stopAmbience(fadeSeconds = 0.12): void {
    const handle = this.ambience;
    const context = this.context;
    if (!handle || !context) return;
    this.ambience = null;
    const now = context.currentTime;
    const fade = Math.max(0, fadeSeconds);
    if (fade > 0) rampGain(handle.mix.gain, now, SILENCE, fade);
    else handle.mix.gain.setValueAtTime(0, now);
    const stopAt = now + fade;
    for (const source of handle.sources) {
      try {
        source.stop(stopAt);
      } catch {
        // Source may already have ended during teardown.
      }
    }
    const finalSource = handle.sources[handle.sources.length - 1];
    finalSource.addEventListener(
      'ended',
      () => {
        for (const source of handle.sources) disconnectSafely(source);
        for (const node of handle.nodes) disconnectSafely(node);
      },
      { once: true },
    );
    this.notifyState();
  }

  dispose(): void {
    if (this.disposedValue) return;
    this.removeUnlockListeners();
    this.removeVisibilityListener();
    this.stopAmbience(0);
    const context = this.context;
    if (context) {
      for (const voice of [...this.activeVoices.values()]) this.stopVoice(voice, 0);
      context.onstatechange = null;
      void context.close().catch((error: unknown) => this.reportError(error));
    }
    for (const gain of this.groupGains.values()) disconnectSafely(gain);
    disconnectSafely(this.masterGain);
    disconnectSafely(this.compressor);
    this.groupGains.clear();
    this.cooldowns.clear();
    this.variants.clear();
    this.whiteNoise = null;
    this.brownNoise = null;
    this.masterGain = null;
    this.compressor = null;
    this.context = null;
    this.unlockedValue = false;
    this.disposedValue = true;
    this.notifyState();
    this.listeners.clear();
  }

  private async performUnlock(): Promise<void> {
    try {
      if (!this.context) this.createAudioGraph();
      const context = this.context;
      if (!context) return;
      if (context.state !== 'running') await context.resume();
      this.unlockedValue = context.state === 'running';
      if (this.unlockedValue) {
        this.removeUnlockListeners();
        this.applyMasterGain(0.02);
        this.startAmbience();
      }
      this.notifyState();
    } catch (error) {
      this.reportError(error);
    }
  }

  private createAudioGraph(): void {
    const AudioContextClass = getAudioContextConstructor();
    if (!AudioContextClass) {
      this.supportedValue = false;
      return;
    }
    const context = new AudioContextClass();
    const master = context.createGain();
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -12;
    compressor.knee.value = 18;
    compressor.ratio.value = 5;
    compressor.attack.value = 0.004;
    compressor.release.value = 0.16;
    master.connect(compressor).connect(context.destination);

    for (const group of AUDIO_GROUPS) {
      const gain = context.createGain();
      gain.gain.value = this.groupVolumesValue[group];
      gain.connect(master);
      this.groupGains.set(group, gain);
    }

    this.context = context;
    this.masterGain = master;
    this.compressor = compressor;
    this.whiteNoise = createNoiseBuffer(context, 2.4, false, 0x4d3a2f17);
    this.brownNoise = createNoiseBuffer(context, 4.8, true, 0x71c90b5d);
    this.applyMasterGain(0);
    context.onstatechange = () => this.notifyState();
  }

  private prepareSound(
    cooldownKey: string,
    cooldownSeconds: number,
    allowWhilePaused = false,
  ): AudioContext | null {
    const context = this.context;
    if (
      !context ||
      context.state === 'closed' ||
      this.disposedValue ||
      this.mutedValue ||
      this.pageHiddenValue ||
      (this.pausedValue && !allowWhilePaused)
    ) {
      return null;
    }
    const now = context.currentTime;
    const previous = this.cooldowns.get(cooldownKey) ?? -Infinity;
    if (now - previous < cooldownSeconds) return null;
    this.cooldowns.set(cooldownKey, now);
    return context;
  }

  private beginVoice(
    context: AudioContext,
    group: AudioGroup,
    priority: number,
    pan: number,
  ): ActiveVoice | null {
    const groupGain = this.groupGains.get(group);
    if (!groupGain) return null;
    if (this.activeVoices.size >= this.maxVoicesValue) {
      const candidate = [...this.activeVoices.values()].sort(
        (left, right) => left.priority - right.priority || left.startedAt - right.startedAt,
      )[0];
      if (!candidate || candidate.priority > priority) return null;
      this.stopVoice(candidate, 0.012);
    }

    const input = context.createGain();
    input.gain.value = 1;
    const panner = context.createStereoPanner();
    panner.pan.value = clamp(pan, -1, 1);
    input.connect(panner).connect(groupGain);
    const voice: ActiveVoice = {
      id: this.nextVoiceId,
      priority,
      startedAt: context.currentTime,
      input,
      panner,
      sources: new Set(),
    };
    this.nextVoiceId += 1;
    this.activeVoices.set(voice.id, voice);
    return voice;
  }

  private addTone(voice: ActiveVoice, options: ToneOptions): void {
    const context = this.context;
    if (!context) return;
    const start = context.currentTime + (options.delay ?? 0);
    const end = start + options.duration;
    const oscillator = context.createOscillator();
    oscillator.type = options.type ?? 'sine';
    oscillator.detune.value = options.detune ?? 0;
    oscillator.frequency.setValueAtTime(Math.max(20, options.frequency), start);
    if (options.endFrequency !== undefined) {
      oscillator.frequency.exponentialRampToValueAtTime(
        Math.max(20, options.endFrequency),
        end,
      );
    }
    const gain = context.createGain();
    scheduleEnvelope(
      gain.gain,
      start,
      Math.min(options.attack ?? 0.006, options.duration * 0.35),
      options.gain,
      end,
    );
    oscillator.connect(gain).connect(voice.input);
    this.registerSource(voice, oscillator, [gain]);
    oscillator.start(start);
    oscillator.stop(end + 0.025);
  }

  private addNoise(voice: ActiveVoice, options: NoiseOptions): void {
    const context = this.context;
    const buffer = options.kind === 'brown' ? this.brownNoise : this.whiteNoise;
    if (!context || !buffer) return;
    const start = context.currentTime + (options.delay ?? 0);
    const end = start + options.duration;
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = options.playbackRate ?? 1;
    const filter = context.createBiquadFilter();
    filter.type = options.filterType ?? 'bandpass';
    filter.frequency.value = options.frequency ?? 1_400;
    filter.Q.value = options.q ?? 0.8;
    const gain = context.createGain();
    scheduleEnvelope(
      gain.gain,
      start,
      Math.min(options.attack ?? 0.004, options.duration * 0.35),
      options.gain,
      end,
    );
    source.connect(filter).connect(gain).connect(voice.input);
    this.registerSource(voice, source, [filter, gain]);
    source.start(start);
    source.stop(end + 0.025);
  }

  private registerSource(
    voice: ActiveVoice,
    source: AudioScheduledSourceNode,
    nodes: readonly AudioNode[],
  ): void {
    voice.sources.add(source);
    source.addEventListener(
      'ended',
      () => {
        disconnectSafely(source);
        for (const node of nodes) disconnectSafely(node);
        voice.sources.delete(source);
        if (voice.sources.size === 0) this.cleanupVoice(voice);
      },
      { once: true },
    );
  }

  private stopVoice(voice: ActiveVoice, fadeSeconds: number): void {
    const context = this.context;
    if (!context) return;
    this.activeVoices.delete(voice.id);
    const now = context.currentTime;
    rampGain(voice.input.gain, now, SILENCE, fadeSeconds);
    for (const source of voice.sources) {
      try {
        source.stop(now + fadeSeconds);
      } catch {
        // Already stopped.
      }
    }
    if (voice.sources.size === 0) this.cleanupVoice(voice);
  }

  private cleanupVoice(voice: ActiveVoice): void {
    this.activeVoices.delete(voice.id);
    disconnectSafely(voice.input);
    disconnectSafely(voice.panner);
  }

  private playErrorCue(key: string, startFrequency: number, endFrequency: number): void {
    const context = this.prepareSound(key, 0.22, true);
    if (!context) return;
    const voice = this.beginVoice(context, 'ui', 6, 0);
    if (!voice) return;
    this.addTone(voice, {
      duration: 0.24,
      frequency: startFrequency,
      endFrequency,
      gain: 0.075,
      type: 'sawtooth',
    });
    this.addTone(voice, {
      delay: 0.03,
      duration: 0.2,
      frequency: startFrequency * 1.48,
      endFrequency: endFrequency * 1.36,
      gain: 0.026,
      type: 'square',
    });
  }

  private playPauseCue(resuming: boolean): void {
    const context = this.prepareSound(resuming ? 'resume' : 'pause', 0.12, true);
    if (!context) return;
    const voice = this.beginVoice(context, 'ui', 4, 0);
    if (!voice) return;
    const first = resuming ? 390 : 560;
    const second = resuming ? 560 : 390;
    this.addTone(voice, {
      duration: 0.11,
      frequency: first,
      gain: 0.05,
      type: 'triangle',
    });
    this.addTone(voice, {
      delay: 0.075,
      duration: 0.14,
      frequency: second,
      gain: 0.055,
      type: 'triangle',
    });
  }

  private applyMasterGain(fadeSeconds: number): void {
    if (!this.context || !this.masterGain) return;
    const target = this.mutedValue ? 0 : this.masterVolumeValue;
    rampGain(this.masterGain.gain, this.context.currentTime, target, fadeSeconds);
  }

  private async resumeContextAndAmbience(): Promise<void> {
    const context = this.context;
    if (!context || this.disposedValue || this.pageHiddenValue) return;
    try {
      if (context.state !== 'running') await context.resume();
      this.startAmbience();
      this.notifyState();
    } catch (error) {
      this.reportError(error);
    }
  }

  private nextVariant(key: string, count: number): number {
    const current = this.variants.get(key) ?? 0;
    this.variants.set(key, current + 1);
    return current % count;
  }

  private installUnlockListeners(): void {
    const windowTarget = getWindow();
    if (!windowTarget || this.unlockListenersInstalled || !this.supportedValue) return;
    this.unlockListenersInstalled = true;
    windowTarget.addEventListener('pointerdown', this.handleUnlockGesture, {
      capture: true,
      passive: true,
    });
    windowTarget.addEventListener('keydown', this.handleUnlockGesture, { capture: true });
  }

  private removeUnlockListeners(): void {
    const windowTarget = getWindow();
    if (!windowTarget || !this.unlockListenersInstalled) return;
    this.unlockListenersInstalled = false;
    windowTarget.removeEventListener('pointerdown', this.handleUnlockGesture, true);
    windowTarget.removeEventListener('keydown', this.handleUnlockGesture, true);
  }

  private installVisibilityListener(): void {
    const documentTarget = getDocument();
    if (!documentTarget || this.visibilityListenerInstalled) return;
    this.visibilityListenerInstalled = true;
    documentTarget.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private removeVisibilityListener(): void {
    const documentTarget = getDocument();
    if (!documentTarget || !this.visibilityListenerInstalled) return;
    this.visibilityListenerInstalled = false;
    documentTarget.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  private readContextState(): AudioContextStatus {
    if (!this.supportedValue) return 'unavailable';
    const state = this.context?.state;
    if (!state) return 'suspended';
    return state as AudioContextStatus;
  }

  private notifyState(): void {
    const state = this.getState();
    for (const listener of [...this.listeners]) {
      this.invokeSafely(() => listener(state));
    }
  }

  private invokeSafely(action: () => void): void {
    try {
      action();
    } catch (error) {
      this.reportError(error);
    }
  }

  private reportError(error: unknown): void {
    if (!this.onError) return;
    try {
      this.onError(error);
    } catch {
      // Audio/UI reporting must never interrupt gameplay.
    }
  }

  private assertActive(): void {
    if (this.disposedValue) throw new Error('AudioSystem has been disposed.');
  }
}

function createNoiseBuffer(
  context: AudioContext,
  durationSeconds: number,
  brown: boolean,
  seed: number,
): AudioBuffer {
  const length = Math.ceil(context.sampleRate * durationSeconds);
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const samples = buffer.getChannelData(0);
  let randomState = seed >>> 0;
  let previous = 0;
  for (let index = 0; index < samples.length; index += 1) {
    randomState ^= randomState << 13;
    randomState ^= randomState >>> 17;
    randomState ^= randomState << 5;
    const white = ((randomState >>> 0) / 2_147_483_648 - 1) * 0.86;
    if (brown) {
      previous = (previous + 0.018 * white) / 1.018;
      samples[index] = previous * 3.25;
    } else {
      samples[index] = white;
    }
  }
  return buffer;
}

function scheduleEnvelope(
  parameter: AudioParam,
  start: number,
  attack: number,
  peak: number,
  end: number,
): void {
  parameter.setValueAtTime(SILENCE, start);
  parameter.exponentialRampToValueAtTime(Math.max(SILENCE, peak), start + Math.max(0.001, attack));
  parameter.exponentialRampToValueAtTime(SILENCE, end);
}

function rampGain(
  parameter: AudioParam,
  now: number,
  target: number,
  duration: number,
): void {
  parameter.cancelScheduledValues(now);
  parameter.setValueAtTime(parameter.value, now);
  parameter.linearRampToValueAtTime(target, now + Math.max(0.001, duration));
}

function getAudioContextConstructor(): typeof AudioContext | null {
  const windowTarget = getWindow();
  if (!windowTarget) return null;
  const audioWindow = windowTarget as Window & {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  return audioWindow.AudioContext ?? audioWindow.webkitAudioContext ?? null;
}

function getWindow(): Window | null {
  return typeof window === 'undefined' ? null : window;
}

function getDocument(): Document | null {
  return typeof document === 'undefined' ? null : document;
}

function assertAudioGroup(group: string): asserts group is AudioGroup {
  if (!(AUDIO_GROUPS as readonly string[]).includes(group)) {
    throw new Error(`Unknown audio group: ${group}`);
  }
}

function assertLane(lane: string): asserts lane is AudioLane {
  if (lane !== 'left' && lane !== 'center' && lane !== 'right') {
    throw new Error(`Unknown audio lane: ${lane}`);
  }
}

function readVolume(value: number, label: string): number {
  if (!Number.isFinite(value) || value < 0 || value > 1) {
    throw new RangeError(`${label} must be a finite number from 0 through 1.`);
  }
  return value;
}

function readIntegerInRange(value: number, min: number, max: number, label: string): number {
  if (!Number.isInteger(value) || value < min || value > max) {
    throw new RangeError(`${label} must be an integer from ${min} through ${max}.`);
  }
  return value;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function disconnectSafely(node: AudioNode | null): void {
  if (!node) return;
  try {
    node.disconnect();
  } catch {
    // Already disconnected.
  }
}
